import { useCallback, useEffect, useRef, useState } from "react";
import { Edit3, Loader2, PackagePlus, RefreshCw, Trash2, X } from "lucide-react";
import { toast } from "react-toastify";
import { metaCatalogConnectionApi } from "../../api/metaCatalogConnectionApi";

const defaults = {
  retailerId: "",
  name: "",
  description: "",
  price: "",
  currency: "INR",
  availability: "in stock",
  imageUrl: "",
  url: "https://xplorebyte.com/",
  brand: "XploreByte",
  condition: "new",
};

const fieldClass = "mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100";
const labelClass = "text-xs font-semibold text-slate-700";

const fromProduct = product => ({
  ...defaults,
  ...product,
  price:
    product?.priceMinorUnits === null || product?.priceMinorUnits === undefined
      ? ""
      : String(product.priceMinorUnits / 100),
  imageUrl: product?.imageUrl ?? "",
  description: product?.description ?? "",
  url: product?.url ?? defaults.url,
  brand: product?.brand ?? defaults.brand,
  condition: product?.condition ?? defaults.condition,
});

const payload = form => ({
  retailerId: form.retailerId.trim(),
  name: form.name.trim(),
  description: form.description.trim() || null,
  price: Number(form.price),
  currency: form.currency.trim().toUpperCase(),
  availability: form.availability,
  imageUrl: form.imageUrl.trim() || null,
  url: form.url.trim(),
  brand: form.brand.trim(),
  condition: form.condition,
});

function ProductEditor({ product, busy, onCancel, onSave }) {
  const [form, setForm] = useState(() => fromProduct(product));
  const set = (name, value) => setForm(current => ({ ...current, [name]: value }));
  const submit = event => {
    event.preventDefault();
    onSave(payload(form));
  };
  return (
    <form onSubmit={submit} className="mt-3 grid grid-cols-1 gap-3 border-t border-slate-200 pt-3 sm:grid-cols-2" aria-label={product ? "Edit Meta product" : "Create Meta product"}>
      <label className={labelClass}>Retailer ID
        <input className={fieldClass} value={form.retailerId} disabled={Boolean(product)} required maxLength={100} onChange={e => set("retailerId", e.target.value)} />
      </label>
      <label className={labelClass}>Product name
        <input className={fieldClass} value={form.name} required maxLength={100} onChange={e => set("name", e.target.value)} />
      </label>
      <label className={`${labelClass} sm:col-span-2`}>Description
        <textarea className={fieldClass} value={form.description} maxLength={5000} rows={2} onChange={e => set("description", e.target.value)} />
      </label>
      <label className={labelClass}>Price
        <input className={fieldClass} value={form.price} type="number" min="0" step="0.01" required onChange={e => set("price", e.target.value)} />
      </label>
      <label className={labelClass}>Currency
        <input className={fieldClass} value={form.currency} pattern="[A-Za-z]{3}" maxLength={3} required onChange={e => set("currency", e.target.value)} />
      </label>
      <label className={labelClass}>Availability
        <select className={fieldClass} value={form.availability} onChange={e => set("availability", e.target.value)}>
          <option value="in stock">In stock</option><option value="out of stock">Out of stock</option>
          <option value="preorder">Preorder</option><option value="available for order">Available for order</option>
          <option value="discontinued">Discontinued</option>
        </select>
      </label>
      <label className={labelClass}>Condition
        <select className={fieldClass} value={form.condition} onChange={e => set("condition", e.target.value)}>
          <option value="new">New</option><option value="refurbished">Refurbished</option><option value="used">Used</option>
        </select>
      </label>
      <label className={labelClass}>Brand
        <input className={fieldClass} value={form.brand} required maxLength={100} onChange={e => set("brand", e.target.value)} />
      </label>
      <label className={labelClass}>Product URL
        <input className={fieldClass} value={form.url} type="url" required onChange={e => set("url", e.target.value)} />
      </label>
      <label className={`${labelClass} sm:col-span-2`}>Public HTTPS image URL
        <input className={fieldClass} value={form.imageUrl} type="url" onChange={e => set("imageUrl", e.target.value)} />
      </label>
      <div className="flex justify-end gap-2 sm:col-span-2">
        <button type="button" onClick={onCancel} className="inline-flex h-9 items-center gap-1.5 rounded-md border border-slate-300 px-3 text-sm"><X size={14} /> Cancel</button>
        <button type="submit" disabled={busy} className="inline-flex h-9 items-center gap-1.5 rounded-md bg-emerald-700 px-3 text-sm font-semibold text-white disabled:opacity-50">
          {busy && <Loader2 size={14} className="animate-spin" />} {product ? "Save product" : "Create product"}
        </button>
      </div>
    </form>
  );
}

export default function MetaCatalogProductsPanel({ canCreate, canDelete, onRevoked }) {
  const [page, setPage] = useState({ items: [], nextCursor: null });
  const [editor, setEditor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const mutationPending = useRef(false);

  const fail = useCallback(errorValue => {
    setError(errorValue.message);
    if (["META_CATALOG_ACCESS_REVOKED", "META_CATALOG_CREDENTIAL_UNAVAILABLE"].includes(errorValue.code)) onRevoked?.();
  }, [onRevoked]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setPage(await metaCatalogConnectionApi.listProducts(25)); }
    catch (requestError) { fail(requestError); }
    finally { setLoading(false); }
  }, [fail]);

  useEffect(() => { load(); }, [load]);

  const save = async values => {
    if (mutationPending.current) return;
    mutationPending.current = true;
    setBusy("save"); setError("");
    try {
      if (editor?.id) await metaCatalogConnectionApi.updateProduct(editor.id, values);
      else await metaCatalogConnectionApi.createProduct(values);
      toast.success(editor?.id ? "Meta product updated." : "Meta product created.");
      setEditor(null); await load();
    } catch (requestError) { fail(requestError); }
    finally { mutationPending.current = false; setBusy(""); }
  };

  const remove = async product => {
    if (mutationPending.current || !window.confirm(`Delete ${product.name} from the connected Meta catalogue?`)) return;
    mutationPending.current = true;
    setBusy(product.id); setError("");
    try {
      await metaCatalogConnectionApi.deleteProduct(product.id);
      toast.success("Meta product deleted."); await load();
    } catch (requestError) { fail(requestError); }
    finally { mutationPending.current = false; setBusy(""); }
  };

  return (
    <div className="mt-3 border-t border-slate-200 pt-3" aria-label="Meta catalogue products">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><h3 className="text-sm font-semibold text-slate-900">Meta products</h3><p className="text-[11px] text-slate-500">Direct management is available for connection verification. XploreByte Catalogue synchronization is the next phase.</p></div>
        <div className="flex gap-1">
          <button type="button" onClick={load} disabled={loading || Boolean(busy)} aria-label="Refresh Meta products" className="rounded p-2 text-slate-600 hover:bg-slate-100 disabled:opacity-40"><RefreshCw size={15} /></button>
          {canCreate && !editor && <button type="button" onClick={() => setEditor({})} className="inline-flex h-8 items-center gap-1.5 rounded-md bg-emerald-700 px-3 text-xs font-semibold text-white"><PackagePlus size={14} /> Create product</button>}
        </div>
      </div>
      {error && <p role="alert" className="mt-2 text-xs font-medium text-red-700">{error}</p>}
      {loading ? <div className="flex justify-center py-5"><Loader2 className="animate-spin text-slate-500" size={18} /></div> : page.items.length === 0 ? <p className="py-5 text-center text-xs text-slate-500">No Meta products are available.</p> : (
        <div className="mt-3 divide-y divide-slate-100 border-y border-slate-100">
          {page.items.map(product => <div key={product.id} className="flex items-center justify-between gap-3 py-2.5">
            <div className="flex min-w-0 items-center gap-3">
              {product.imageUrl ? <img src={product.imageUrl} alt="" className="h-10 w-10 shrink-0 rounded object-cover" /> : <span className="h-10 w-10 shrink-0 rounded bg-slate-100" />}
              <div className="min-w-0"><p className="truncate text-sm font-medium text-slate-900">{product.name}</p><p className="truncate text-xs text-slate-500">{product.retailerId} · {product.currency || ""} {product.priceMinorUnits == null ? "Unpriced" : (product.priceMinorUnits / 100).toLocaleString()}</p></div>
            </div>
            <div className="flex shrink-0 gap-1">
              {canCreate && <button type="button" onClick={() => setEditor(product)} aria-label={`Edit ${product.name}`} className="rounded p-1.5 text-slate-600 hover:bg-slate-100"><Edit3 size={15} /></button>}
              {canDelete && <button type="button" disabled={busy === product.id} onClick={() => remove(product)} aria-label={`Delete ${product.name}`} className="rounded p-1.5 text-red-700 hover:bg-red-50 disabled:opacity-40">{busy === product.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}</button>}
            </div>
          </div>)}
        </div>
      )}
      {editor && <ProductEditor product={editor.id ? editor : null} busy={busy === "save"} onCancel={() => setEditor(null)} onSave={save} />}
    </div>
  );
}
